import React from 'react';
import styled from '@emotion/styled';
import { Flex, Box } from 'reflexbox';

import { SvgFooterOpenLearning } from './images/FooterOpenLearning';
import { SvgFacebook } from './images/Facebook';
import { SvgLinkedin } from './images/Linkedin';
import { SvgTwitter } from './images/Twitter';
import { SvgYoutube } from './images/Youtube';

import {
  MARKETING_PAGE_BREAKPOINT_TABLET,
  MARKETING_PAGE_BREAKPOINT_DESKTOP,
} from '../../Style/breakpoints';
import {
  ROUTE_CERTIFICATE_VERIFICATION_PAGE,
  ROUTE_CONTACT_PAGE,
  ROUTE_CREATE_COURSE_PAGE,
  ROUTE_SSO,
  ROUTE_COURSES,
  ROUTE_PARTNERS_LISTING_PAGE,
} from '../../../../ReduxStorage/routeActions';
import {
  HELP_ORIGIN,
  SOLUTIONS_ORIGIN,
  TOS_GENERAL_URL,
  PRIVACY_POLICY_URL,
} from '../../../../constants';
import { RouteLink } from '../../RouteLink/component';

const ContainerWrapper = styled.div`
  background: #002b3e;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const Container = styled(Flex)`
  flex-direction: column;
  width: 100%;
  padding: 12px;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_TABLET}) {
    width: 100%;
    max-width: 1180px;
    padding: 90px 60px;
    flex-direction: initial;
    justify-content: space-between;
  }
`;

const TermsContainer = styled.div`
  background: #002332;
  padding: 12px 0 24px 0;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_TABLET}) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    padding: 24px 0px;
    flex-direction: initial;
    justify-content: space-between;
  }
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
`;

const TermsContainerInner = styled.div`
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    width: 1170px;
    padding-left: 60px;
    padding-right: 60px;
    box-sizing: border-box;
  }
`;

const SocialHeading = styled(Box)({
  fontWeight: 'normal',
  fontSize: '16px',
  lineHeight: '135%',
  color: '#B7BCBD',
  padding: '24px 0',
});

const Header = styled(Box)`
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  line-height: 120%;
  letter-spacing: -0.01em;
  color: #f6f8f8;
  margin-bottom: 12px;
`;

const Column = styled(Flex)`
  flex-direction: column;
  padding: 12px;
`;

const StyledText = styled(Box)`
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 190%;
  color: #b0b6c2;
`;

export const Footer: React.FC = () => {
  const FooterInternalLink = styled(RouteLink)({
    color: '#B7BCBD',
    fontSize: '14px',
    lineHeight: '190%',
    fontWeight: 'normal',

    ':hover': {
      color: '#FFFFFF',
    },
  });

  const FooterExternalLink = styled.a({
    color: '#B7BCBD',
    fontSize: '14px',
    lineHeight: '190%',
    fontWeight: 'normal',

    ':hover': {
      color: '#FFFFFF',
    },
  });

  const copyrightYear = new Date().getFullYear();

  return (
    <ContainerWrapper>
      <Container>
        <Column width="270px">
          <Box>
            <SvgFooterOpenLearning />
          </Box>
          <SocialHeading>
            OpenLearning is a scalable online learning platform at the forefront
            of a new wave of education delivery; enabling educators and
            institutions to prepare their learners for the future of work.
          </SocialHeading>
          <Flex justifyContent="space-between">
            <a href="https://www.facebook.com/OpenLearningAUS/">
              <SvgFacebook />
            </a>
            <a href="https://twitter.com/openlrning">
              <SvgTwitter />
            </a>
            <a href="https://www.linkedin.com/company/openlearningcom/">
              <SvgLinkedin />
            </a>
            <a href="https://www.youtube.com/channel/UCmPXu01nxGz39r0fOTEbzpg">
              <SvgYoutube />
            </a>
          </Flex>
        </Column>
        <Column>
          <Header>Tools and resources</Header>
          <FooterInternalLink
            to={{
              type: ROUTE_SSO,
              params: {
                action: 'help',
              },
              query: {
                url: `${HELP_ORIGIN}/`,
              },
            }}
            target="_blank"
          >
            Help & Support
          </FooterInternalLink>
          <FooterInternalLink
            to={{
              type: ROUTE_CONTACT_PAGE,
            }}
          >
            Contact us
          </FooterInternalLink>
          <FooterExternalLink
            href="https://medium.com/learning-designers-toolkit"
            target="_blank"
          >
            Learning design toolkit
          </FooterExternalLink>
          <FooterInternalLink
            to={{
              type: ROUTE_CERTIFICATE_VERIFICATION_PAGE,
            }}
          >
            Verify a certificate
          </FooterInternalLink>
          <FooterInternalLink
            to={{
              type: ROUTE_SSO,
              params: {
                action: 'help',
              },
              query: {
                url: `${HELP_ORIGIN}/category/integrations`,
              },
            }}
            target="_blank"
          >
            Integrations
          </FooterInternalLink>
          <FooterExternalLink
            href="https://status.openlearning.com/"
            target="_blank"
          >
            Status
          </FooterExternalLink>
        </Column>
        <Column>
          <Header>Platform</Header>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/learning-philosophy/`}>
            Philosophy
          </FooterExternalLink>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/platform-features/`}>
            Features
          </FooterExternalLink>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/pricing/`}>
            Pricing
          </FooterExternalLink>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/opencreds/`}>
            OpenCreds
          </FooterExternalLink>
          <FooterInternalLink
            to={{
              type: ROUTE_PARTNERS_LISTING_PAGE,
            }}
          >
            Partners
          </FooterInternalLink>
          <FooterInternalLink
            to={{
              type: ROUTE_COURSES,
            }}
          >
            Browse all courses
          </FooterInternalLink>
          <FooterInternalLink
            to={{
              type: ROUTE_CREATE_COURSE_PAGE,
            }}
          >
            Create a course
          </FooterInternalLink>
        </Column>
        <Column>
          <Header>Company</Header>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/about/`}>
            About us
          </FooterExternalLink>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/teams/`}>
            Team
          </FooterExternalLink>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/careers/`}>
            Careers
          </FooterExternalLink>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/press/`}>
            Press
          </FooterExternalLink>
          <FooterExternalLink href={`${SOLUTIONS_ORIGIN}/investor-home/`}>
            Investors
          </FooterExternalLink>
          <FooterExternalLink
            href={`${SOLUTIONS_ORIGIN}/contact`}
            target="_blank"
          >
            Partnerships
          </FooterExternalLink>
        </Column>
      </Container>
      <TermsContainer>
        <TermsContainerInner>
          <Box color="#B0B6C2" p={12} display="inline-block">
            <StyledText>
              © {`${copyrightYear}`} OpenLearning Limited (ASX: OLL)
            </StyledText>
          </Box>
          <Box p={12} display="inline-block">
            <FooterExternalLink
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy policy
            </FooterExternalLink>
          </Box>
          <Box p={12} display="inline-block">
            <FooterExternalLink
              href={TOS_GENERAL_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of service
            </FooterExternalLink>
          </Box>
        </TermsContainerInner>
      </TermsContainer>
    </ContainerWrapper>
  );
};
